import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  Badge,
  Fab,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SnackbarProvider, useSnackbar } from "notistack";
import notificationSoundNewOrder from "../../../notification/Tesoro.mp3";

// Icons
import ArrowDownwardIcon from "@mui/icons-material/KeyboardArrowDown";
import ClearIcon from "@mui/icons-material/Clear";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import EmojiPeopleTwoToneIcon from '@mui/icons-material/EmojiPeopleTwoTone';

import { DashboardContext } from "../../../contexts/dashboard.context";

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed !important",
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1300,
    backgroundColor: theme.palette.secondary.main,
    color: "#fff",
  },
  snackbarContainer: {
    bottom: 120,
  },
  notificationContent: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#1e1e1e",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "12px",
    padding: "8px 16px",
    minWidth: "240px",
    boxShadow: "0px 8px 24px rgba(0,0,0,0.5)",
    pointerEvents: "auto",
  },
  textContainer: {
    flexGrow: 1,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  notificationText: {
    color: "#fff",
    fontSize: "0.95rem",
    fontWeight: 700,
    lineHeight: 1,
  },
  subText: {
    color: "#aaa",
    fontSize: "0.75rem",
    marginTop: "4px",
  },
  iconTable: {
    color: theme.palette.primary.light,
    marginRight: 12,
  },
  closeBtn: {
    color: "rgba(255,255,255,0.4)",
    marginLeft: 8,
  },
  "@keyframes ring": {
    "0%": { transform: "rotate(0) scale(1)" },
    "10%": { transform: "rotate(25deg) scale(1.15)" },
    "20%": { transform: "rotate(-25deg) scale(1.25)" },
    "30%": { transform: "rotate(30deg) scale(1.35)" },
    "40%": { transform: "rotate(-30deg) scale(1.35)" },
    "50%": { transform: "rotate(25deg) scale(1.25)" },
    "60%": { transform: "rotate(-20deg) scale(1.15)" },
    "70%": { transform: "rotate(15deg)" },
    "80%": { transform: "rotate(-10deg)" },
    "90%": { transform: "rotate(5deg)" },
    "100%": { transform: "rotate(0)" },
  },
  ringBell: {
    animation: "$ring 0.9s ease-in-out",
  },
}));

function DashboardNotification({ isClickedOut, setIsClickedOut }) {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { data, setData, discardCalledTable, lastGeneralUpdate } = useContext(DashboardContext);

  const [showAll, setShowAll] = useState(false);
  const [ringKey, setRingKey] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Refs per tracciare stato
  const openSnacks = useRef(new Set()); // Quali ID sono a video
  const lastSoundState = useRef({});    // Per il suono
  const lastSoundTime = useRef({});     // Timer 10s

  const activeCalls = useMemo(
    () => (data ? data.filter((t) => t.tableId && (t.isCalled || t.hasNewOrder)) : []),
    [data]
  );

  // 1. LOGICA SUONO (Sempre attiva in background)
  useEffect(() => {
    let shouldRing = false;

    activeCalls.forEach((table) => {
      if (!table.hasNewOrder) return; // ignora chiamate cameriere

      const tableId = table.tableId;
      const orderSignature = `${table.hasNewOrder}-${table.reservations?.length || 0}`;

      // Suoniamo SOLO se cambia lo stato dell’ordine
      if (lastSoundState.current[tableId] !== orderSignature) {
        lastSoundState.current[tableId] = orderSignature;
        shouldRing = true;

        const now = Date.now();
        const lastTime = lastSoundTime.current[tableId] || 0;

        if (now - lastTime > 10000) {
          new Audio(notificationSoundNewOrder).play().catch(() => { });
          lastSoundTime.current[tableId] = now;
        }
      }
    });

    if (shouldRing) {
      setRingKey((k) => k + 1);
    }
  }, [activeCalls, lastGeneralUpdate]);

  // 2. DISMISS (Chiude tutto per quel tavolo)
  const handleDismissType = useCallback(
    (key, tableId, type) => {
      if (key) closeSnackbar(key);
      openSnacks.current.delete(key);

      // Aggiorniamo il context in base a cosa stiamo chiudendo
      setData((prev) =>
        prev.map((t) => {
          if (t.tableId === tableId) {
            const update = type === "call" ? { isCalled: false } : { hasNewOrder: false };
            // Se stiamo spegnendo la chiamata, avvisiamo il server
            if (type === "call") discardCalledTable(tableId);
            return { ...t, ...update };
          }
          return t;
        })
      );
    },
    [closeSnackbar, discardCalledTable, setData]
  );

  // 3. LOGICA VISUALE (Gestione priorità e aggiornamento)
  useEffect(() => {
    if (showAll) {
      activeCalls.forEach((table) => {
        const tableId = table.tableId;
        //const resCount = table.reservations?.length || 0;

        // --- GESTIONE NOTIFICA CHIAMATA ---
        if (table.isCalled) {
          const callKey = `call-${tableId}`;

          if (!openSnacks.current.has(callKey)) {
            openSnacks.current.add(callKey);

            enqueueSnackbar(`Tavolo ${tableId}`, {
              key: callKey,
              persist: true,
              content: (key) => (
                <div className={classes.notificationContent}>
                  <EmojiPeopleTwoToneIcon className={classes.iconTable} />
                  <div className={classes.textContainer}>
                    <Typography className={classes.notificationText}>
                      Tavolo {tableId}
                    </Typography>
                    <Typography className={classes.subText}>
                      Richiede cameriere
                    </Typography>
                  </div>
                  <IconButton
                    className={classes.closeBtn}
                    onClick={() =>
                      handleDismissType(key, tableId, "call")
                    }
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            });
          }
        }

        // --- GESTIONE NOTIFICA ORDINE (UNA SOLA PER TAVOLO) ---
        if (table.hasNewOrder) {
          const orderKey = `order-${tableId}`;

          // 👉 CREA SOLO SE NON ESISTE
          if (!openSnacks.current.has(orderKey)) {
            openSnacks.current.add(orderKey);

            enqueueSnackbar("Nuovo Ordine", {
              key: orderKey,
              persist: true,
              content: (key) => (
                <div className={classes.notificationContent}>
                  <RestaurantMenuIcon className={classes.iconTable} />
                  <div className={classes.textContainer}>

                    <Typography className={classes.notificationText}>
                      Tavolo {tableId}
                    </Typography>
                    <Typography className={classes.subText}>
                      Nuovo Ordine
                    </Typography>
                  </div>
                  <IconButton
                    className={classes.closeBtn}
                    onClick={() =>
                      handleDismissType(key, tableId, "order")
                    }
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            });
          }
        }
      });
    } else {
      // Chiudiamo tutto quando il FAB si richiude
      openSnacks.current.forEach((key) => closeSnackbar(key));
      openSnacks.current.clear();
    }
  }, [
    showAll,
    activeCalls,
    enqueueSnackbar,
    handleDismissType,
    classes,
    closeSnackbar,
  ]);


  // Chiusura click esterno
  useEffect(() => {
    if (isClickedOut) {
      setShowAll(false);
      setIsClickedOut(false);
    }
  }, [isClickedOut, setIsClickedOut]);

  // Chiusura automatica se vuoto
  useEffect(() => {
    if (activeCalls.length === 0 && showAll) {
      setShowAll(false);
    }
  }, [activeCalls.length, showAll]);

  const handleFabClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setShowAll((v) => !v);
    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <Fab
      className={classes.fab}
      onClick={handleFabClick}
      disabled={activeCalls.length === 0}
    >
      <Badge
        badgeContent={activeCalls.length}
        color="secondary"
        overlap="circular"
        sx={{ "& .MuiBadge-badge": { transform: "translate(100%, -100%)" } }}
      >
        {showAll ? (
          <ArrowDownwardIcon />
        ) : (
          <NotificationsActiveIcon key={ringKey} className={classes.ringBell} />
        )}
      </Badge>
    </Fab>
  );
}

const NotistackWrapper = (props) => {
  const classes = useStyles();
  return (
    <SnackbarProvider
      maxSnack={8}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      classes={{ containerAnchorOriginBottomRight: classes.snackbarContainer }}
    >
      <DashboardNotification {...props} />
    </SnackbarProvider>
  );
};
export default NotistackWrapper;